import {
    AppStoreAvailableBuild,
    AutomationRun,
    CompactFeaturesStepData,
    DestinationPhasedRelease,
    ReleaseStatus,
    ReleaseStepType,
    ReleaseTimelinePhase,
    ReleaseType,
    RolloutMonitoring,
    Statistic,
    TimelineEvent,
} from '@/types';
import { ReleaseDelay } from '@/types/releaseDelay';
import { Destination } from './destinations';
import { Nullable } from '@/typescriptHelpers';
import { WorkflowRunStepRunGraph } from './workflowRunStepRunGraph';

export interface WorkflowOverviewWorkflowRun {
    workflowId: string;
    workflowRunId: string;
    appId: string;
    releaseId: string;

    version: string;
    displayableVersion: string;

    type: ReleaseType;
    timelinePhase: ReleaseTimelinePhase;
    status: ReleaseStatus;

    overviewDisplayStatus: WorkflowRunOverviewDisplayStatus;
    destinationStatusByDestinationStepId: Record<string, OverviewDestinationStatus>;

    targetKickoffDate: Nullable<string>;
    targetSubmitDate: Nullable<string>;
    targetReleaseDate: Nullable<string>;

    createdAt: string;
    updatedAt: string;
    completedAt: Nullable<string>;
    skippedAt: Nullable<string>;

    kickedOffAt: Nullable<string>;
    submittedAt: Nullable<string>;
    startedReviewAt: Nullable<string>;
    releasedAt: Nullable<string>;

    submittedAtByDestinationStepId: Record<string, string>;
    startedReviewAtByDestinationStepId: Record<string, string>;
    releasedAtByDestinationStepId: Record<string, string>;
    releaseDelaysByDestinationStepId?: Record<string, ReleaseDelay>;

    workflowPlanningSummary: Nullable<string>;
    workflowSummaryMessage: Nullable<string>;
    releasePilotId: Nullable<string>;

    workflowRunGraph: WorkflowRunStepRunGraph;
    archetypeGroupIdToGroupDetailsMap: Record<string, ArchetypeGroupSummaryDetails>;
    stepRunIdToDestinationStepConfigIdsMap: Record<string, string[]>;

    events: TimelineEvent[];
    stats: Statistic[];
    lifecycleAutomations: Nullable<OverviewLifecycleAutomationAPIs>;

    featuresStep: CompactFeaturesStepData;
    rolloutMonitoringByDestinationStepId: Record<string, RolloutMonitoring>;
    phasedReleaseInfosByDestinationStepId: Record<string, DestinationPhasedRelease>;
    selectedAppStoreBuildsByDestinationStepId: Record<string, AppStoreAvailableBuild[]>;

    phasedReleaseWidgetGroups: DestinationPhasedRelease[][];
}

export interface WorkflowOverview {
    workflowId: string;
    appId: string;
    workflowRuns: WorkflowOverviewWorkflowRun[];
    workflowDestinationStepIds: string[];
    selectedDestinationStepIds: string[];
    destinationInfoByDestinationStepId: Record<string, Destination>;
}

export interface OverviewLifecycleAutomationAPIs {
    legacyLifecycleAutomations?: AutomationRun[];
    workflowLifecycleAutomationsByDestination?: Record<string, AutomationRun[]>;
}

export enum WorkflowRunOverviewDisplayStatus {
    Upcoming = 'upcoming',
    Active = 'active',
    RollingOut = 'rollingOut',
    Completed = 'completed',
    Skipped = 'skipped',
}

export enum OverviewDestinationStatus {
    Missing = 'missing',
    Inactive = 'inactive',
    Skipped = 'skipped',
    Upcoming = 'upcoming',
    Preparing = 'preparing',
    Submitted = 'submitted',
    InReview = 'inReview',
    Approved = 'approved',
    Rejected = 'rejected',
    DeveloperRejected = 'developerRejected',
    RolloutInProgress = 'rolloutInProgress',
    RolloutHalted = 'rolloutHalted',
    RolloutCompleted = 'rolloutCompleted',
    CompletedFullRelease = 'completedFullRelease',
    CompletedNotReleased = 'completedNotReleased',
}

export const DestinationStatusOrderIndices: Record<OverviewDestinationStatus, number> = {
    completedFullRelease: 0,
    completedNotReleased: 1,
    rolloutCompleted: 2,
    rolloutInProgress: 3,
    rolloutHalted: 4,
    approved: 5,
    rejected: 6,
    inReview: 7,
    developerRejected: 8,
    submitted: 9,
    preparing: 10,
    upcoming: 11,
    skipped: 12,
    inactive: 13,
    missing: 14,
};

export interface ArchetypeGroupSummaryDetails {
    parentStepGroupIndex: number;
    archetype: ReleaseStepType;
    archetypeGroupId: string;
    archetypeGroupStepConfigIds: string[];
    archetypeGroupStepRunIds: string[];
}
